import * as React from 'react';
import { isEqual } from 'vet-bones/utils';

import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { projectAttributesByIriSelector } from 'src/selectors/projectAttributesByIri';
import { projectFileUploadsByIdSelector } from 'src/selectors/projectFileUploadsById';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import { projectLinkDataArraySelector } from 'src/selectors/projectLinkDataArray';
import { projectNodeDataArraySelector } from 'src/selectors/projectNodeDataArray';
import { projectSensitivePropertyGroupsByNameSelector } from 'src/selectors/projectSensitivePropertyGroupsByName';
import { projectVirtualGraphsByIdSelector } from 'src/selectors/projectVirtualGraphsById';
import { EntityDrawerType } from 'src/store/ui/types';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useNavigationValueAndText = () => {
  const { projectId } = useRouterParams();
  const attributesByIri = useReduxSelector(
    projectAttributesByIriSelector(projectId),
    isEqual
  );
  const entityDrawer = useReduxSelector(entityDrawerSelector, isEqual);
  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );
  const linkDataArray = useReduxSelector(
    projectLinkDataArraySelector(projectId),
    isEqual
  );
  const nodeDataArray = useReduxSelector(
    projectNodeDataArraySelector(projectId),
    isEqual
  );
  const projectFileUploadsById = useReduxSelector(
    projectFileUploadsByIdSelector(projectId),
    isEqual
  );
  const projectVirtualGraphsById = useReduxSelector(
    projectVirtualGraphsByIdSelector(projectId),
    isEqual
  );
  const projectSensitivePropertyGroups = useReduxSelector(
    projectSensitivePropertyGroupsByNameSelector(projectId),
    isEqual
  );

  return React.useMemo(() => {
    const { history } = entityDrawer;
    let index = history.length - 1;
    let label: string | null = null;
    let validEntityValue: string | null = null;

    while (!validEntityValue && history[index]) {
      const entry = history[index];
      if (entry.type === EntityDrawerType.ATTRIBUTE) {
        const attribute = attributesByIri[entry.entityValue as string];
        validEntityValue = attribute?.value || null;
        label = attribute ? getLabelForIri(labelsByIri, attribute.value) : null;
      } else if (entry.type === EntityDrawerType.CLASS) {
        const classData = nodeDataArray.find(
          (nodeData) => nodeData.value === entry.entityValue
        );
        validEntityValue = classData?.value || null;
        label = classData ? getLabelForIri(labelsByIri, classData.value) : null;
      } else if (entry.type === EntityDrawerType.RELATIONSHIP) {
        const relationshipData = linkDataArray.find(
          (linkData) => linkData.value === entry.entityValue
        );
        validEntityValue = relationshipData?.value || null;
        label = relationshipData
          ? getLabelForIri(labelsByIri, relationshipData.value)
          : null;
      } else if (entry.type === EntityDrawerType.RESOURCE) {
        const resourceData = nodeDataArray.find(
          (nodeData) => nodeData.value === entry.entityValue
        );
        validEntityValue = resourceData?.value || null;
        if (validEntityValue) {
          const projectResource =
            projectFileUploadsById[validEntityValue] ||
            projectVirtualGraphsById[validEntityValue] ||
            null;
          label = projectResource?.name || null;
        }
      } else if (entry.type === EntityDrawerType.SENSITIVE_PROPERTY_GROUP) {
        const spgName = entry.entityValue || '';
        const hasSpg = Boolean(projectSensitivePropertyGroups[spgName]);
        if (hasSpg) {
          validEntityValue = spgName;
          label = spgName;
        }
      } else {
        // NONE should not be recorded in history
        break;
      }

      if (validEntityValue) {
        break;
      }

      index -= 1;
    }
    return { entityValue: validEntityValue, label };
  }, [
    attributesByIri,
    entityDrawer,
    labelsByIri,
    linkDataArray,
    nodeDataArray,
    projectFileUploadsById,
    projectSensitivePropertyGroups,
    projectVirtualGraphsById,
  ]);
};
